// AIDEV-NOTE: Service camada de abstracao entre UI e API/Dexie
// Tentativa 1: API online -> em caso de erro -> fallback Dexie + op_log
// Garante que pedidos sejam persistidos mesmo offline

import { db } from '../../../db/dexie';
import type { Pedido as DexiePedido } from '../../../db/dexie';
import { opLogService } from '../../../db/op-log.service';
import api from '../../../services/auth.service';
import type {
  Pedido,
  CreatePedidoDto,
  FinalizarPedidoDto,
  AddItemDto,
  StatusPedido
} from '@azoupvendas/shared';
import { podeExcluirPedido } from '@azoupvendas/shared';

// -- Tipos de retorno --

interface CriarPedidoResult {
  id: string;
  offline: boolean;
}

interface FinalizarPedidoResult {
  success: boolean;
  offline: boolean;
  // AIDEV-NOTE: pedido sempre presente — online vem da API, offline e montado localmente a partir
  // do dto + contexto do wizard. O ShareModal/PDF precisa dele e roda 100% client-side.
  pedido: Pedido;
}

// AIDEV-NOTE: dados que so o wizard conhece (nome/CNPJ do cliente, totais ja calculados com a
// condicao de pagamento). Usados para montar o Pedido local quando o finalizar cai offline —
// recalcular aqui divergiria do que o vendedor viu na tela de confirmacao.
export interface ContextoLocalPedido {
  cliente_codigo: number;
  cliente_nome: string;
  cliente_cpf_cnpj?: string | null;
  tabela_preco_id?: number | null;
  total_produtos?: number;
  total_final?: number;
}

interface ExcluirPedidoResult {
  success: boolean;
  offline: boolean;
}

// -- Helpers --

// AIDEV-NOTE: Detecta se o erro indica falta de conectividade.
// Criterio central: o request NAO obteve resposta do servidor. navigator.onLine e um sinal fraco —
// no campo o cenario comum nao e "sem rede", e sim 5G oscilando: onLine === true, o request estoura
// o timeout de 30s (ECONNABORTED) ou morre na camada de rede, e nenhum byte volta do servidor.
// Antes desta versao so !onLine e ERR_NETWORK contavam, entao o fallback Dexie + op_log nunca
// disparava com sinal fraco e o vendedor perdia o pedido digitado (relato de campo 2026-10).
// AIDEV-SECURITY: erro COM response (4xx/5xx) nunca pode ser classificado como offline — seria
// gravar localmente uma operacao que o servidor recusou, e o vendedor acharia que fechou o pedido.
function isOfflineError(error: unknown): boolean {
  if (!navigator.onLine) return true;

  if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
    return true;
  }

  if (typeof error !== 'object' || error === null) return false;

  const err = error as { isAxiosError?: boolean; code?: string; response?: unknown };

  // Resposta do servidor = erro de negocio, nao de conectividade
  if (err.response) return false;

  // Axios sem response: ERR_NETWORK (rede caiu) ou ECONNABORTED/ETIMEDOUT (timeout de 30s)
  if (err.isAxiosError) return true;

  return err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT';
}

// AIDEV-NOTE: soma dos itens com desconto por item (sem desconto geral nem condicao de pagamento)
function totalDosItens(
  itens: Array<
    Pick<AddItemDto, 'quantidade' | 'valor_unitario'> & { desconto_percentual?: number | null }
  >
): number {
  return itens.reduce(
    (sum, item) =>
      sum + item.quantidade * item.valor_unitario * (1 - (item.desconto_percentual ?? 0) / 100),
    0
  );
}

// AIDEV-NOTE: Monta um Pedido no formato da API a partir do que o wizard enviou — usado quando o
// finalizar cai no caminho offline. numero: null sinaliza "ainda nao sincronizado" (o ShareModal
// imprime "(Rascunho)" no lugar do numero do ERP, que so existe apos o push pro Firebird).
function montarPedidoLocal(
  pedidoId: string,
  dto: FinalizarPedidoDto,
  vendedorId: number,
  ctx?: ContextoLocalPedido
): Pedido {
  const totalProdutos = ctx?.total_produtos ?? totalDosItens(dto.itens);
  const agora = new Date().toISOString();

  return {
    id: pedidoId,
    client_id: 0, // preenchido pelo server apos sync
    vendedor_id: vendedorId,
    cliente_codigo: ctx?.cliente_codigo ?? 0,
    cliente_nome: ctx?.cliente_nome ?? '',
    cliente_cpf_cnpj: ctx?.cliente_cpf_cnpj ?? '',
    numero: null,
    status: 'finalizado',
    origem: dto.origem ?? 'app',
    tabela_preco_id: ctx?.tabela_preco_id ?? null,
    condicao_pagto_id: dto.condicao_pagto_id ?? null,
    desconto_geral_percentual: dto.desconto_geral_percentual ?? 0,
    observacao: dto.observacao ?? null,
    total_produtos: totalProdutos,
    total_desconto: 0,
    total_final: ctx?.total_final ?? totalProdutos,
    itens: dto.itens.map((item) => ({
      produto_codigo: item.produto_codigo,
      referencia: item.referencia ?? '',
      descricao: item.descricao,
      cor: item.cor ?? null,
      tamanho: item.tamanho ?? null,
      quantidade: item.quantidade,
      valor_unitario: item.valor_unitario,
      desconto_percentual: item.desconto_percentual ?? 0,
      total_item:
        item.quantidade * item.valor_unitario * (1 - (item.desconto_percentual ?? 0) / 100)
    })),
    parcelas: dto.parcelas ?? [],
    sync_attempts: 0,
    sync_error: null,
    id_pedido_integracao: null,
    created_at: agora,
    updated_at: agora
  } as Pedido;
}

// AIDEV-NOTE: op_log NAO tem indice [entity_type+entity_id] — usa indice entity_type + filtro JS
async function getOpsDoPedido(pedidoId: string) {
  return db.op_log
    .where('entity_type')
    .equals('pedidos')
    .filter((op) => op.entity_id === pedidoId)
    .toArray();
}

// AIDEV-NOTE: Converte Pedido da API (datas string) para formato Dexie (timestamps number)
// AIDEV-NOTE: itens defensivo — POST /pedidos (create) retorna so o header (RETURNING * de
// pedidos, sem itens); assumir itens presente quebrou toda criacao online em prod (2026-08-04).
function apiPedidoToDexie(pedido: Pedido, vendedorId: number): DexiePedido {
  return {
    id: pedido.id,
    numero: pedido.numero ?? undefined,
    cliente_id: pedido.cliente_codigo,
    vendedor_id: vendedorId,
    status: pedido.status,
    itens: (pedido.itens ?? []).map((item) => ({
      produto_id: item.produto_codigo,
      descricao: item.descricao,
      quantidade: item.quantidade,
      preco_unitario: item.valor_unitario,
      desconto: item.desconto_percentual
    })),
    valor_total: pedido.total_final,
    desconto_total: pedido.total_desconto,
    observacoes: pedido.observacao ?? undefined,
    created_at: pedido.created_at ? new Date(pedido.created_at).getTime() : Date.now(),
    updated_at: pedido.updated_at ? new Date(pedido.updated_at).getTime() : Date.now(),
    synced_at: undefined,
    tabela_preco_id: pedido.tabela_preco_id ?? undefined
  };
}

// -- Service --

class PedidoClientService {
  /**
   * Cria pedido: tenta API, se offline salva no Dexie + adiciona ao op_log
   * AIDEV-NOTE: O pedido offline recebe status 'rascunho' e numero null (sera atribuido apos sync)
   */
  async criarPedido(
    dto: CreatePedidoDto & { itens: AddItemDto[] },
    vendedorId: number
  ): Promise<CriarPedidoResult> {
    // Tentativa online
    try {
      if (!navigator.onLine) {
        throw new TypeError('Failed to fetch');
      }

      const response = await api.post<Pedido | { pedido: Pedido }>('/pedidos', dto);
      // AIDEV-NOTE: endpoint devolve { pedido }, nao Pedido direto — normaliza os dois formatos
      const pedido = (response.data as { pedido?: Pedido }).pedido ?? (response.data as Pedido);

      // Salva no Dexie tambem (cache local)
      const dexiePedido = apiPedidoToDexie(pedido, vendedorId);
      await db.pedidos.put(dexiePedido);

      return { id: pedido.id, offline: false };
    } catch (error) {
      if (!isOfflineError(error)) {
        throw error;
      }
    }

    // AIDEV-NOTE: Fallback offline — salva no Dexie + op_log atomicamente
    const offlineId = crypto.randomUUID();
    const now = Date.now();

    const dexiePedido: DexiePedido = {
      id: offlineId,
      cliente_id: dto.cliente_codigo,
      vendedor_id: vendedorId,
      status: 'rascunho',
      itens: dto.itens.map((item) => ({
        produto_id: item.produto_codigo,
        descricao: item.descricao,
        quantidade: item.quantidade,
        preco_unitario: item.valor_unitario,
        desconto: item.desconto_percentual
      })),
      valor_total: dto.itens.reduce((sum, item) => {
        const descFactor = 1 - (item.desconto_percentual ?? 0) / 100;
        return sum + item.quantidade * item.valor_unitario * descFactor;
      }, 0),
      observacoes: undefined,
      created_at: now,
      updated_at: now
    };

    // AIDEV-SECURITY: Payload e encriptado via AES-GCM pelo opLogService antes de persistir
    await db.transaction('rw', [db.pedidos, db.op_log], async () => {
      await db.pedidos.put(dexiePedido);

      await opLogService.log({
        operation_type: 'CREATE_PEDIDO',
        entity_type: 'pedidos',
        entity_id: offlineId,
        payload: {
          ...dto,
          id: offlineId,
          vendedor_id: vendedorId
        } as unknown as Record<string, unknown>,
        priority: 8 // Pedidos tem alta prioridade
      });
    });

    return { id: offlineId, offline: true };
  }

  /**
   * Finaliza pedido: tenta API, se offline atualiza Dexie + adiciona ao op_log
   * AIDEV-NOTE: Muda status para 'finalizado' localmente; sync engine cuidara de enviar ao Firebird
   */
  async finalizarPedido(
    pedidoId: string,
    dto: FinalizarPedidoDto,
    vendedorId: number,
    contextoLocal?: ContextoLocalPedido
  ): Promise<FinalizarPedidoResult> {
    // Tentativa online
    try {
      if (!navigator.onLine) {
        throw new TypeError('Failed to fetch');
      }

      const response = await api.post<Pedido | { pedido: Pedido }>(
        `/pedidos/${pedidoId}/finalizar`,
        dto
      );
      // AIDEV-NOTE: endpoint devolve { pedido }, nao Pedido direto — normaliza os dois formatos
      const pedido =
        (response.data as { pedido?: Pedido }).pedido ?? (response.data as Pedido) ?? null;

      // Atualiza cache local
      await db.pedidos.update(pedidoId, {
        status: 'finalizado' as const,
        updated_at: Date.now()
      });

      return {
        success: true,
        offline: false,
        pedido: pedido ?? montarPedidoLocal(pedidoId, dto, vendedorId, contextoLocal)
      };
    } catch (error) {
      if (!isOfflineError(error)) {
        throw error;
      }
    }

    // AIDEV-SECURITY: Dedupe de finalizacao — precedente do incidente de 2026-08-21 (pedido de
    // 244 itens entrou 5x no ERP). O primeiro clique em "Finalizar" leva ate 30s ate o timeout;
    // se o vendedor clicar de novo, cada clique enfileirava um FINALIZAR_PEDIDO e o drain mandaria
    // N POSTs /finalizar do mesmo pedido. Mantem apenas a finalizacao mais recente — o CREATE do
    // pedido offline NAO e tocado (sem ele o servidor nem conhece o pedido).
    const finalizacoesPendentes = (await getOpsDoPedido(pedidoId)).filter(
      (op) => op.operation_type === 'FINALIZAR_PEDIDO' && op.status !== 'synced'
    );

    // AIDEV-NOTE: Fallback offline — atualiza Dexie + op_log
    // AIDEV-SECURITY: Payload e encriptado via AES-GCM pelo opLogService
    await db.transaction('rw', [db.pedidos, db.op_log], async () => {
      if (finalizacoesPendentes.length > 0) {
        await db.op_log.bulkDelete(finalizacoesPendentes.map((op) => op.id));
      }

      // AIDEV-NOTE: grava tambem itens e total — sem isso o pedido finalizado offline aparecia
      // zerado/vazio na lista local ate a sincronizacao, e o vendedor achava que tinha perdido.
      await db.pedidos.update(pedidoId, {
        status: 'finalizado' as const,
        itens: dto.itens.map((item) => ({
          produto_id: item.produto_codigo,
          descricao: item.descricao,
          quantidade: item.quantidade,
          preco_unitario: item.valor_unitario,
          desconto: item.desconto_percentual
        })),
        valor_total: contextoLocal?.total_final ?? totalDosItens(dto.itens),
        updated_at: Date.now()
      });

      await opLogService.log({
        operation_type: 'FINALIZAR_PEDIDO',
        entity_type: 'pedidos',
        entity_id: pedidoId,
        payload: {
          id: pedidoId,
          vendedor_id: vendedorId,
          ...dto
        } as unknown as Record<string, unknown>,
        // AIDEV-NOTE: mesma prioridade do CREATE — ordem causal garantida pelo desempate por created_at
        priority: 8
      });
    });

    return {
      success: true,
      offline: true,
      pedido: montarPedidoLocal(pedidoId, dto, vendedorId, contextoLocal)
    };
  }

  /**
   * Lista pedidos: merge API + Dexie (pedidos offline que ainda nao sincronizaram)
   * AIDEV-NOTE: Pedidos do Dexie com status 'rascunho' e sem synced_at sao considerados offline
   */
  async listarPedidos(vendedorId: number, filters?: { status?: StatusPedido }): Promise<Pedido[]> {
    let apiPedidos: Pedido[] = [];

    // Tenta buscar da API
    try {
      if (navigator.onLine) {
        const params: Record<string, string> = {};
        if (filters?.status) {
          params.status = filters.status;
        }

        const response = await api.get<Pedido[]>('/pedidos', { params });
        apiPedidos = response.data;

        // Atualiza cache local com dados da API
        for (const pedido of apiPedidos) {
          const dexiePedido = apiPedidoToDexie(pedido, vendedorId);
          dexiePedido.synced_at = Date.now();
          await db.pedidos.put(dexiePedido);
        }
      }
    } catch (error) {
      if (!isOfflineError(error)) {
        throw error;
      }
      // Se offline, apiPedidos fica vazio — usaremos apenas Dexie
    }

    // Busca pedidos offline (nao sincronizados) do Dexie
    let offlinePedidos = await db.pedidos.where('vendedor_id').equals(vendedorId).toArray();

    // Filtra por status se especificado
    if (filters?.status) {
      offlinePedidos = offlinePedidos.filter((p) => p.status === filters.status);
    }

    // Se estamos offline, converte tudo do Dexie para o formato Pedido
    if (apiPedidos.length === 0) {
      return offlinePedidos.map((p) => dexiePedidoToApi(p));
    }

    // Merge: adiciona pedidos offline que nao estao na resposta da API
    const apiIds = new Set(apiPedidos.map((p) => p.id));
    const pendingOffline = offlinePedidos.filter((p) => !apiIds.has(p.id) && !p.synced_at);

    return [...apiPedidos, ...pendingOffline.map((p) => dexiePedidoToApi(p))];
  }

  /**
   * Busca um pedido por ID — tenta API, fallback Dexie
   */
  async buscarPedido(pedidoId: string, vendedorId: number): Promise<Pedido | null> {
    // Tentativa online
    try {
      if (navigator.onLine) {
        const response = await api.get<Pedido | { pedido: Pedido }>(`/pedidos/${pedidoId}`);
        // AIDEV-NOTE: o endpoint devolve { pedido }, nao Pedido direto — normaliza os dois
        // formatos (mesmo tropeco que ja tinha quebrado a criacao online em 2026-08).
        const pedido = (response.data as { pedido?: Pedido }).pedido ?? (response.data as Pedido);

        // Atualiza cache local
        const dexiePedido = apiPedidoToDexie(pedido, vendedorId);
        dexiePedido.synced_at = Date.now();
        await db.pedidos.put(dexiePedido);

        return pedido;
      }
    } catch (error) {
      if (!isOfflineError(error)) {
        throw error;
      }
    }

    // Fallback Dexie
    const dexiePedido = await db.pedidos.get(pedidoId);
    if (!dexiePedido) return null;

    return dexiePedidoToApi(dexiePedido);
  }

  /**
   * Pedidos que existem so no aparelho — ainda nao confirmados pelo servidor
   * AIDEV-NOTE: a lista e o detalhe de pedidos batem direto na API. Sem este resgate, o pedido
   * fechado sem sinal sumia da tela ate o drain rodar, e o vendedor concluia que tinha perdido o
   * trabalho (era exatamente a queixa de campo). Ordenado pelo mais recente primeiro.
   */
  async listarPedidosLocaisPendentes(vendedorId: number): Promise<Pedido[]> {
    const locais = await db.pedidos.where('vendedor_id').equals(vendedorId).toArray();

    return locais
      .filter((p) => !p.synced_at)
      .sort((a, b) => b.updated_at - a.updated_at)
      .map((p) => dexiePedidoToApi(p));
  }

  /**
   * Exclui pedido em rascunho: tenta API, se offline remove do Dexie + trata op_log
   * AIDEV-NOTE: 2 caminhos — online (DELETE API); offline (cancela ops nao-sincronizadas do
   * pedido e enfileira DELETE_PEDIDO sempre, pois o servidor pode ja conhecer o pedido)
   */
  async excluirPedido(pedidoId: string, vendedorId: number): Promise<ExcluirPedidoResult> {
    // Tentativa online
    try {
      if (!navigator.onLine) {
        throw new TypeError('Failed to fetch');
      }

      await api.delete(`/pedidos/${pedidoId}`);

      // Remove do cache local + ops pendentes orfas do pedido
      const ops = await getOpsDoPedido(pedidoId);
      await db.transaction('rw', [db.pedidos, db.op_log], async () => {
        await db.pedidos.delete(pedidoId);
        const naoSincronizadas = ops.filter((op) => op.status !== 'synced');
        if (naoSincronizadas.length > 0) {
          await db.op_log.bulkDelete(naoSincronizadas.map((op) => op.id));
        }
      });

      return { success: true, offline: false };
    } catch (error) {
      // Erros de negocio do servidor (400/404) sobem para a UI exibir
      if (!isOfflineError(error)) {
        throw error;
      }
    }

    // AIDEV-SECURITY: Espelha a regra do servidor no client — offline nao ha defesa do backend.
    // podeExcluirPedido (@shared) e a mesma funcao usada por pedidos.repository.deletePedido.
    const pedidoLocal = await db.pedidos.get(pedidoId);
    if (pedidoLocal && !podeExcluirPedido(pedidoLocal)) {
      throw new Error('Este pedido nao pode ser excluido');
    }

    // AIDEV-NOTE: Fallback offline — enfileira DELETE_PEDIDO SEMPRE, mesmo se havia CREATE_PEDIDO
    // pendente/failed: com o drain funcionando, o servidor pode ja conhecer o pedido (CREATE
    // parcial: POST ok mas PATCH de itens falhou 3x; ou corrida com o drain que ja capturou a op
    // no batch). O handler do drain trata 404 como sucesso, entao o DELETE e inocuo quando o
    // servidor de fato nunca soube do pedido.
    const opsDoPedido = await getOpsDoPedido(pedidoId);

    // AIDEV-SECURITY: Payload e encriptado via AES-GCM pelo opLogService antes de persistir
    await db.transaction('rw', [db.pedidos, db.op_log], async () => {
      await db.pedidos.delete(pedidoId);

      const naoSincronizadas = opsDoPedido.filter((op) => op.status !== 'synced');
      if (naoSincronizadas.length > 0) {
        await db.op_log.bulkDelete(naoSincronizadas.map((op) => op.id));
      }

      await opLogService.log({
        operation_type: 'DELETE_PEDIDO',
        entity_type: 'pedidos',
        entity_id: pedidoId,
        payload: { id: pedidoId, vendedor_id: vendedorId },
        priority: 8 // Pedidos tem alta prioridade
      });
    });

    return { success: true, offline: true };
  }

  /**
   * Retry de sync para pedido com erro_sync
   * AIDEV-NOTE: Chama endpoint dedicado do sync engine
   */
  async retrySyncPedido(pedidoId: string): Promise<void> {
    await api.post(`/sync/pedido/${pedidoId}`);
  }
}

// AIDEV-NOTE: Converte formato Dexie (local) para formato Pedido (API/shared)
function dexiePedidoToApi(p: DexiePedido): Pedido {
  return {
    id: p.id,
    client_id: 0, // Sera preenchido pelo server apos sync
    vendedor_id: p.vendedor_id,
    cliente_codigo: p.cliente_id,
    cliente_nome: '', // AIDEV-TODO: Buscar nome do cliente no Dexie via cliente_id
    cliente_cpf_cnpj: '',
    numero: p.numero ?? null,
    status: p.status,
    origem: 'app',
    tabela_preco_id: p.tabela_preco_id ?? null,
    condicao_pagto_id: null,
    desconto_geral_percentual: 0,
    observacao: p.observacoes ?? null,
    total_produtos: p.valor_total,
    total_desconto: p.desconto_total ?? 0,
    total_final: p.valor_total,
    itens: p.itens.map((item) => ({
      produto_codigo: item.produto_id,
      referencia: '',
      descricao: item.descricao,
      cor: null,
      tamanho: null,
      quantidade: item.quantidade,
      valor_unitario: item.preco_unitario,
      desconto_percentual: item.desconto ?? 0,
      total_item: item.quantidade * item.preco_unitario * (1 - (item.desconto ?? 0) / 100)
    })),
    parcelas: [],
    sync_attempts: 0,
    sync_error: null,
    id_pedido_integracao: null,
    created_at: new Date(p.created_at).toISOString(),
    updated_at: new Date(p.updated_at).toISOString()
  };
}

// Singleton
export const pedidoClientService = new PedidoClientService();
